/**
 * Point matching utilities for pairing points between coordinate systems
 */

import type {
  SystemPoint,
  PointPair,
  MatchingMode,
  AutoMatchResult,
} from '@/types';

/**
 * Match points using exact point number matching
 */
const matchExact = (sys1: SystemPoint[], sys2: SystemPoint[]): PointPair[] => {
  const pairs: PointPair[] = [];
  let id = 1;

  sys1.forEach((p1) => {
    const match = sys2.find((p2) => p2.point === p1.point);
    if (match) {
      pairs.push({
        id: id++,
        label: `${p1.point}-${match.point}`,
        sys1Point: p1.point,
        sys2Point: match.point,
        n1: p1.north,
        e1: p1.east,
        n2: match.north,
        e2: match.east,
        weight: 1,
        desc1: p1.description,
        desc2: match.description,
      });
    }
  });

  return pairs;
};

/**
 * Match points using prefix/suffix matching
 * (e.g., 402 matches 2, or PT402 matches PT2)
 */
const matchPrefix = (sys1: SystemPoint[], sys2: SystemPoint[]): PointPair[] => {
  const pairs: PointPair[] = [];
  let id = 1;
  const usedSys2 = new Set<string>();

  sys1.forEach((p1) => {
    const num1 = p1.point.replace(/\D/g, '');
    if (!num1) return;
    const match = sys2.find((p2) => {
      if (usedSys2.has(p2.point)) return false;
      const num2 = p2.point.replace(/\D/g, '');
      if (!num2) return false;
      return num1 === num2 || num1.endsWith(num2) || num2.endsWith(num1);
    });

    if (match && !usedSys2.has(match.point)) {
      usedSys2.add(match.point);
      pairs.push({
        id: id++,
        label: `${p1.point}-${match.point}`,
        sys1Point: p1.point,
        sys2Point: match.point,
        n1: p1.north,
        e1: p1.east,
        n2: match.north,
        e2: match.east,
        weight: 1,
        desc1: p1.description,
        desc2: match.description,
      });
    }
  });

  return pairs;
};

/**
 * Match points using nearest coordinate (proximity matching)
 */
const matchNearest = (
  sys1: SystemPoint[],
  sys2: SystemPoint[]
): PointPair[] => {
  const pairs: PointPair[] = [];
  let id = 1;
  const usedSys2 = new Set<string>();

  for (const p1 of sys1) {
    let bestMatch: SystemPoint | null = null;
    let bestDist = Infinity;

    for (const p2 of sys2) {
      if (usedSys2.has(p2.point)) continue;

      const dn = parseFloat(p1.north) - parseFloat(p2.north);
      const de = parseFloat(p1.east) - parseFloat(p2.east);
      const dist = Math.sqrt(dn * dn + de * de);

      if (dist < bestDist) {
        bestDist = dist;
        bestMatch = p2;
      }
    }

    if (bestMatch !== null) {
      usedSys2.add(bestMatch.point);
      pairs.push({
        id: id++,
        label: `${p1.point}-${bestMatch.point}`,
        sys1Point: p1.point,
        sys2Point: bestMatch.point,
        n1: p1.north,
        e1: p1.east,
        n2: bestMatch.north,
        e2: bestMatch.east,
        weight: 1,
        desc1: p1.description,
        desc2: bestMatch.description,
        matchDistance: bestDist,
      });
    }
  }

  return pairs;
};

/**
 * Match points using numeric offset detection
 * (e.g., 402 in sys1 matches 2 in sys2 with offset 400)
 */
const matchOffset = (
  sys1: SystemPoint[],
  sys2: SystemPoint[]
): { pairs: PointPair[]; detectedOffset: number } => {
  // Detect the most common offset
  const offsets: Record<number, number> = {};

  sys1.forEach((p1) => {
    const num1 = parseInt(p1.point.replace(/\D/g, ''));
    if (isNaN(num1)) return;

    sys2.forEach((p2) => {
      const num2 = parseInt(p2.point.replace(/\D/g, ''));
      if (isNaN(num2)) return;

      const offset = num1 - num2;
      offsets[offset] = (offsets[offset] || 0) + 1;
    });
  });

  // Find most common offset
  let bestOffset = 0;
  let bestCount = 0;
  Object.entries(offsets).forEach(([offset, count]) => {
    if (count > bestCount) {
      bestCount = count;
      bestOffset = parseInt(offset);
    }
  });

  // Match using detected offset
  const pairs: PointPair[] = [];
  let id = 1;
  const usedSys2 = new Set<string>();

  sys1.forEach((p1) => {
    const num1 = parseInt(p1.point.replace(/\D/g, ''));
    if (isNaN(num1)) return;

    const targetNum = num1 - bestOffset;
    const match = sys2.find((p2) => {
      if (usedSys2.has(p2.point)) return false;
      const num2 = parseInt(p2.point.replace(/\D/g, ''));
      return num2 === targetNum;
    });

    if (match) {
      usedSys2.add(match.point);
      pairs.push({
        id: id++,
        label: `${p1.point}-${match.point}`,
        sys1Point: p1.point,
        sys2Point: match.point,
        n1: p1.north,
        e1: p1.east,
        n2: match.north,
        e2: match.east,
        weight: 1,
        desc1: p1.description,
        desc2: match.description,
        detectedOffset: bestOffset,
      });
    }
  });

  return { pairs, detectedOffset: bestOffset };
};

/**
 * Auto-match points between two systems
 * Tries multiple strategies and returns the best result
 */
export const autoMatchPoints = (
  sys1: SystemPoint[],
  sys2: SystemPoint[],
  mode: MatchingMode = 'auto'
): AutoMatchResult => {
  if (mode === 'exact') {
    return { pairs: matchExact(sys1, sys2), mode: 'exact' };
  }

  if (mode === 'prefix') {
    return { pairs: matchPrefix(sys1, sys2), mode: 'prefix' };
  }

  if (mode === 'nearest') {
    return { pairs: matchNearest(sys1, sys2), mode: 'nearest' };
  }

  if (mode === 'offset') {
    const { pairs, detectedOffset } = matchOffset(sys1, sys2);
    return { pairs, mode: 'offset', detectedOffset };
  }

  // Auto mode - try multiple strategies
  let bestPairs: PointPair[] = [];
  let bestMode: MatchingMode = 'exact';
  let detectedOffset: number | undefined;

  // Try exact match first
  const exactPairs = matchExact(sys1, sys2);
  if (exactPairs.length > 0) {
    bestPairs = exactPairs;
    bestMode = 'exact';
  }

  // If no exact matches, try offset detection
  if (exactPairs.length === 0) {
    const { pairs: offsetPairs, detectedOffset: offset } = matchOffset(
      sys1,
      sys2
    );
    if (offsetPairs.length >= 2 && offsetPairs.length > bestPairs.length) {
      bestPairs = offsetPairs;
      bestMode = 'offset';
      detectedOffset = offset;
    }
  }

  // If still no good matches, try prefix matching
  if (bestPairs.length === 0) {
    const prefixPairs = matchPrefix(sys1, sys2);
    if (prefixPairs.length > bestPairs.length) {
      bestPairs = prefixPairs;
      bestMode = 'prefix';
    }
  }

  return { pairs: bestPairs, mode: bestMode, detectedOffset };
};
